import type {
  AlertsByDate,
  FormattedGetChannelResponse,
  GetChannelResponse
} from '@library/types/alerts';
import type { DateTimeFormat, SplitTime } from '@library/types/dates';
import { DateTime } from 'luxon';
import { formatWithTimezoneChange } from '@library/utils/dateTime';

export const getSplitTime = (date: DateTime): SplitTime => {
  return {
    hour: formatWithTimezoneChange(date, 'hh'),
    minutes: formatWithTimezoneChange(date, 'mm'),
    amPm: formatWithTimezoneChange(date, 'a') as 'AM' | 'PM'
  };
};

export const getSplitDateTime = (
  date: string,
  dateFormat: string = 'MMMM dd, yyyy',
  timeFormat: string = 'h:mm a'
): DateTimeFormat => {
  const dateTime = DateTime.fromISO(date);

  return {
    date: dateTime.toFormat(dateFormat),
    isoString: date,
    time: dateTime.toFormat(timeFormat)
  };
};

export const convertToMilitaryHour = ({
  amPm,
  hour
}: {
  amPm: 'AM' | 'PM' | '';
  hour: string;
}): number => {
  const hourNum = Number(hour);

  if (hourNum === 12) {
    return amPm === 'AM' ? 0 : 12;
  }

  return amPm === 'PM' ? hourNum + 12 : hourNum;
};

export const formatDate = ({
  date,
  pattern = 'MMM dd yyyy; HH:mm'
}: {
  date: string;
  pattern?: string;
}): string => {
  if (!date) return '';
  return DateTime.fromISO(date).toFormat(pattern);
};

export const formatDateToString = (
  date: Date | null | undefined,
  pattern: string = 'yyyy-MM-dd'
): string => {
  if (!date) return '';
  return DateTime.fromJSDate(date).toFormat(pattern);
};

export function formatDateFromYMD(date?: string): string {
  if (!date) return '';

  const truncatedDate = date.substring(0, 10);
  const [year, month, day] = truncatedDate.split('-');
  return `${month}/${day}/${year}`;
}

export const groupAlertsByDate = (
  messages: GetChannelResponse
): FormattedGetChannelResponse => {
  const grouped: Record<string, AlertsByDate> = {};

  messages.data?.forEach((message) => {
    const date = message.attributes?.created_at?.split('T')[0];
    const createdById = message?.attributes?.created_by_id;

    if (!grouped[date]) {
      grouped[date] = {
        date,
        alerts: []
      };
    }

    const user = messages.included.find(
      (userObj) => Number(userObj.id) === createdById
    );

    grouped[date].alerts.push({
      id: message.id.toString(),
      channelName: message.attributes.text,
      firstName:
        createdById === 0 ? 'System' : `${user?.attributes?.first_name}`,
      lastName:
        createdById === 0 ? 'Message' : `${user?.attributes?.last_name}`,
      imageUrl: `${user?.attributes?.image_url}`,
      text: message.attributes.text,
      timestamp: message.attributes.created_at
    });
  });

  return {
    alerts: Object.values(grouped),
    currentPage: messages.meta?.current_page ?? 1,
    totalPages: messages.meta?.total_pages ?? 0
  };
};

export const isValidDateString = (
  date: string,
  dateFormat: string = 'MM-dd-yyyy'
) => {
  const parsedDate = DateTime.fromFormat(date, dateFormat);

  if (!parsedDate.isValid) {
    return false;
  }

  return true;
};

export function getCountdownString(dateString: string): string {
  if (!dateString) {
    return '';
  }
  const targetDate = new Date(dateString);
  const now = new Date();

  const diff = targetDate.getTime() - now.getTime();

  if (diff <= 0) return '0m 0s';

  const seconds = Math.floor(diff / 1000) % 60;
  const minutes = Math.floor(diff / (1000 * 60)) % 60;
  const hours = Math.floor(diff / (1000 * 60 * 60)) % 24;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  return `${minutes}m ${seconds}s`;
}
